import {
  type Order,
  type OrderAttachment,
  downloadAttachment,
  fileTypeMap,
} from "@repo/api";
import {
  Button,
  ListView,
  ListViewItem,
  Loading,
  Text,
  arrowDownToLineIcon,
  documentIcon,
  linkOutIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import {
  ButtonsContainer,
  LocalizedDateAndTime,
  SectionContainer,
  useSettings,
  useToast,
} from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { useEffect, useState } from "react";

interface OrderAttachmentProps {
  order: Order | undefined;
  isDraft?: boolean;
}

const getFileName = (attachment: OrderAttachment, isDraft?: boolean) => {
  return isDraft ? attachment.fileReference : attachment.originalFileName;
};

export const OrderAttachments = ({ order, isDraft }: OrderAttachmentProps) => {
  const { updateToast } = useToast();
  const { market, language } = useSettings();

  const [loadingButton, setLoadingButton] = useState<{
    id: string | null;
    action: string | null;
  }>({ id: null, action: null });
  const [attachmentsWithStatus, setAttachmentsWithStatus] = useState<
    { attachment: OrderAttachment; isAvailable: boolean; fileName: string }[]
  >([]);

  const [isLoading, setIsLoading] = useState(true);

  const allAttachments = isDraft ? order?.attachments : order?.fileAttachments;

  useEffect(() => {
    const fetchAttachmentsStatus = async () => {
      if (!allAttachments) {
        setIsLoading(false);
        return;
      }

      try {
        const attachments = await Promise.all(
          allAttachments.map(async (attachment) => {
            try {
              await downloadAttachment(market, language, attachment, isDraft);
              return {
                attachment,
                isAvailable: true,
                fileName: getFileName(attachment, isDraft),
              };
            } catch {
              return {
                attachment,
                isAvailable: false,
                fileName: getFileName(attachment, isDraft),
              };
            }
          })
        );

        setAttachmentsWithStatus(attachments);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttachmentsStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  if (!attachmentsWithStatus.length) {
    return <Text>{t("label.no_attachments_found")}</Text>;
  }

  const handleAttachmentActions = async (
    attachment: OrderAttachment,
    action: string
  ) => {
    setLoadingButton({ id: attachment.fileReference, action });
    try {
      const response = await downloadAttachment(
        market,
        language,
        attachment,
        isDraft
      );

      const myFile = new File([response], getFileName(attachment, isDraft), {
        type:
          fileTypeMap.get(attachment.fileType ?? "") ??
          "application/octet-stream",
      });

      const fileURL = document.createElement("a");
      fileURL.target = "_blank";
      fileURL.href = window.URL.createObjectURL(myFile);
      fileURL.download = myFile.name;

      document.body.appendChild(fileURL);
      if (action === "DOWNLOAD") {
        fileURL.click();
        updateToast({
          title: t("prompt.file_downloaded"),
        });
      }
      if (action === "OPEN") {
        window.open(fileURL.href, "_blank");
      }
    } catch (err) {
      const errorTitle =
        action === "DOWNLOAD"
          ? t("prompt.file_download_failed")
          : t("prompt.file_open_failed");
      updateToast({
        title: errorTitle,
        message: getErrorMessage(err),
        type: "error",
      });
    } finally {
      setLoadingButton({ id: null, action: null });
    }
  };

  return (
    <SectionContainer>
      <ListView id="order-attachments" showDivider>
        {attachmentsWithStatus.map(
          ({ attachment, isAvailable, fileName }, index) => (
            <ListViewItem
              key={attachment.fileReference || index}
              leadingIcon={documentIcon}
              title={fileName}
              description={
                <>
                  {t("label.uploaded_on")}{" "}
                  <LocalizedDateAndTime date={attachment.uploadedDate} />
                  {!isAvailable && (
                    <Text color="red">{t("label.attachment_unavailable")}</Text>
                  )}
                </>
              }
              quantityLabel={
                <ButtonsContainer>
                  <Button
                    size="small"
                    iconOnly
                    loading={
                      loadingButton.id === fileName &&
                      loadingButton.action === "OPEN"
                    }
                    ssrIcon={linkOutIcon}
                    disabled={!isAvailable}
                    onClick={() => {
                      handleAttachmentActions(attachment, "OPEN");
                      trackAnalytics("view_attachment", {
                        file: attachment.originalFileName,
                      });
                    }}
                  />
                  <Button
                    size="small"
                    iconOnly
                    loading={
                      loadingButton.id === fileName &&
                      loadingButton.action === "DOWNLOAD"
                    }
                    ssrIcon={arrowDownToLineIcon}
                    disabled={!isAvailable}
                    onClick={() => {
                      handleAttachmentActions(attachment, "DOWNLOAD");
                      trackAnalytics("download_attachment", {
                        file: fileName,
                      });
                    }}
                  />
                </ButtonsContainer>
              }
            />
          )
        )}
      </ListView>
    </SectionContainer>
  );
};
